import React, { useState, useMemo } from 'react';
import { SparkModalPortal, Modal, Tabs, DropdownMenu, TagColored, CardSurface, TooltipTrigger, SparkIcon, Text } from '@spark';
import { colors } from '@/shared/tokens/colors';
import TaggedSample from './TaggedSample';
import { getTextDirectionStyle } from '@/shared/utils/textUtils';
import { getSparkTagVariant } from '@/features/datasets/components/DataSelection/sparkLabelColors';
import { StatusType } from './EntityExtractionsTable';
import '@/features/evaluation/styles/EntityTag.css';
const INVALID_ENTITY_TEXT = 'INVALID ENTITY';
// Build relations for the selected tab by mapping indices to entity texts
const buildRelationsForTab = (response) => {
    const relations = (response?.relations || []);
    const entities = response?.entities || [];
    const indexToEntity = new Map(entities.map(e => [e?.start_index, { content: e?.content, label: e?.label }]));
    return relations.map(r => {
        const sourceEntity = indexToEntity.get(r?.source_start_index);
        const targetEntity = indexToEntity.get(r?.target_start_index);
        const sourceInvalid = !sourceEntity;
        const targetInvalid = !targetEntity;
        return {
            source: sourceInvalid ? INVALID_ENTITY_TEXT : (sourceEntity?.content ?? ''),
            sourceLabel: sourceEntity?.label,
            sourceInvalid,
            target: targetInvalid ? INVALID_ENTITY_TEXT : (targetEntity?.content ?? ''),
            targetLabel: targetEntity?.label,
            targetInvalid,
            type: r?.label,
        };
    });
};

const EntityModal = ({ visible, onClose, sample, comparisonModelName, version, teacherModelProcessing, studentModelProcessing, allEntities, activeTab }) => {
    const [modalActiveTab, setModalActiveTab] = useState('1');
    const [selectedEntityFilter, setSelectedEntityFilter] = useState('all');

    // Detect parse-failure flag from a response object (can arrive as an object or a list)
    const isParseFailed = (resp) => {
        if (!resp) return false;
        if (Array.isArray(resp)) {
            const first = resp[0] || {};
            return !!(first?.is_parse_failed);
        }
        return !!resp?.is_parse_failed;
    };

    const newModelParseFailed = useMemo(() => isParseFailed(sample?.new_model_response), [sample]);
    const oldModelParseFailed = useMemo(
        () => isParseFailed(sample?.teacher_response || sample?.comparison_model_response),
        [sample]
    );

    // Get the actual extracted text when parsing failed
    const getParseFailedDetails = (resp) => {
        if (!resp) return null;
        if (Array.isArray(resp)) {
            const item = resp.find(x => x?.is_parse_failed) || resp[0];
            if (!item) return null;
            if (typeof item?.label === 'string') return item.label;
            if (typeof item === 'string') return item;
            try { return JSON.stringify(item, null, 2); } catch { return String(item); }
        }
        if (resp?.is_parse_failed) {
            const candidate = resp?.label || resp?.raw || resp?.raw_response || resp?.response_text;
            if (typeof candidate === 'string') return candidate;
            try { return JSON.stringify(candidate ?? resp, null, 2); } catch { return String(candidate ?? resp); }
        }
        return null;
    };

    const newModelParseFailedText = useMemo(() => getParseFailedDetails(sample?.new_model_response), [sample]);
    const oldModelParseFailedText = useMemo(
        () => getParseFailedDetails(sample?.teacher_response || sample?.comparison_model_response),
        [sample]
    );

    const studentEmptyInDb = Boolean(sample?.new_model_response_empty_in_db);
    const comparisonEmptyInDb = Boolean(
        sample?.comparison_model_response_empty_in_db || sample?.teacher_response_empty_in_db
    );

    const renderNoResponse = () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: 12 }}>
            <TagColored label="No Response" variant="disabled" />
        </div>
    );

    const renderParseFailedMsg = (modelLabel, details) => (
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', color: colors.red, lineHeight: 1, verticalAlign: 'middle' }}>
                <SparkIcon slug="circle-x" size={14} />
            </span>
            <Text variant="base-regular">
                There were issues extracting the{' '}
                <TooltipTrigger
                    variant="light"
                    popupClassName="entity-parse-failed-tooltip"
                    content={(
                        <div className="optune-scrollbar" style={{ maxHeight: 300, overflowY: 'auto' }}>
                            <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{details || 'No details available'}</pre>
                        </div>
                    )}
                >
                    <strong style={{ cursor: 'pointer' }}>response</strong>
                </TooltipTrigger>{' '}
                with {modelLabel}. update prompt or response interpreter
            </Text>
        </div>
    );

    // Wrong location message builder for rendering outside the Card
    // (definitions placed after filter helpers to avoid initialization ordering issues)

    // Get entities for the current active tab
    const getCurrentTabEntities = useMemo(() => {
        switch (modalActiveTab) {
            case '1': // True Entities
                return sample?.test_response?.entities || [];
            case '2': // SpecificAI V
                return sample?.new_model_response?.entities || [];
            case '3': // Comparison Model or Version
                // When comparing versions, entities are under comparison_model_response
                // Fallback to teacher_response for teacher comparisons
                return (sample?.teacher_response?.entities
                    || sample?.comparison_model_response?.entities
                    || []);
            default:
                return [];
        }
    }, [modalActiveTab, sample]);

    // Get unique entity labels from current tab
    const uniqueEntityLabels = useMemo(() => {
        const labels = getCurrentTabEntities.map(e => e.label).filter(Boolean);
        return [...new Set(labels)].sort();
    }, [getCurrentTabEntities]);

    // Helper: build a map of content-pair -> gold relation label (lowercased)
    const buildGoldContentPairMap = () => {
        const entities = sample?.test_response?.entities || [];
        const indexToEntity = new Map(entities.map(e => [e?.start_index, { content: String(e?.content ?? '').toLowerCase() }]));
        const goldRelations = sample?.test_response?.relations || [];
        const toKey = (a, b) => {
            const x = String(a ?? '').toLowerCase();
            const y = String(b ?? '').toLowerCase();
            return x <= y ? `${x}|${y}` : `${y}|${x}`;
        };
        const map = new Map();
        goldRelations.forEach(r => {
            const a = indexToEntity.get(r?.source_start_index)?.content ?? '';
            const b = indexToEntity.get(r?.target_start_index)?.content ?? '';
            map.set(toKey(a, b), String(r?.label || '').toLowerCase());
        });
        return { map, toKey };
    };

    // Relations list for the current tab when relations modal is active
    const relationsForActiveTab = useMemo(() => {
        const { map: goldByContentPair, toKey } = buildGoldContentPairMap();
        const resp = (modalActiveTab === '1')
            ? sample?.test_response
            : (modalActiveTab === '2')
                ? sample?.new_model_response
                : (sample?.teacher_response || sample?.comparison_model_response);

        const base = buildRelationsForTab(resp);

        // Annotate each relation with error type compared to gold (by entity content only)
        return base.map(rel => {
            if (modalActiveTab === '1') {
                return { ...rel, errorType: null };
            }
            const key = toKey(rel.source, rel.target);
            const goldLabel = goldByContentPair.get(key);
            const predictedLabel = String(rel?.type || '').toLowerCase();
            // For 'other' (no relation), do not mark as fabricated or error
            if (predictedLabel === 'other') {
                return { ...rel, errorType: null };
            }
            if (!goldLabel || goldLabel === 'other') {
                return { ...rel, errorType: 'fabricated', tooltip: 'Fabricated relation' };
            }
            if (goldLabel === predictedLabel) {
                return { ...rel, errorType: null };
            }
            return { ...rel, errorType: 'miscategorized', tooltip: 'Miscategorised relation' };
        });
    }, [modalActiveTab, sample]);

    // Count relation types in the currently selected response
    const relationTypesCount = useMemo(() => {
        const counts = {};
        relationsForActiveTab.forEach(r => {
            const label = r?.type;
            if (!label) return;
            counts[label] = (counts[label] || 0) + 1;
        });
        return counts;
    }, [relationsForActiveTab]);

    // Count occurrences per label in the current tab
    const labelCounts = useMemo(() => {
        const counts = {};
        for (const ent of getCurrentTabEntities) {
            const label = ent?.label;
            if (!label) continue;
            counts[label] = (counts[label] || 0) + 1;
        }
        return counts;
    }, [getCurrentTabEntities]);


    // Filter entities based on selected filter
    const getFilteredEntities = (nerResponse) => {
        if (!nerResponse) {
            return [];
        }

        if (selectedEntityFilter === 'all') {
            return nerResponse.entities;
        }
        return nerResponse.entities.filter(entity => entity.label === selectedEntityFilter);
    };

    // Filter entity statuses (used by model tabs)
    const getFilteredEntityStatuses = (statuses) => {
        if (!Array.isArray(statuses)) {
            return [];
        }
        if (selectedEntityFilter === 'all') {
            return statuses;
        }
        return statuses.filter(s => (s?.predicted_label || s?.expected_label) === selectedEntityFilter);
    };

    // Now that filter helpers are defined, compute wrong-location details and renderer
    const getWrongLocations = (statuses) => {
        if (!Array.isArray(statuses)) return [];
        return statuses.filter(s => s?.status === StatusType.WRONG_LOCATION);
    };

    const newModelWrongLocations = useMemo(
        () => getWrongLocations(getFilteredEntityStatuses(sample?.new_model_entities_status || [])),
        [sample, selectedEntityFilter]
    );
    const oldModelWrongLocations = useMemo(
        () => getWrongLocations(getFilteredEntityStatuses(sample?.teacher_model_entities_status || sample?.comparison_model_entities_status || [])),
        [sample, selectedEntityFilter]
    );

    const renderWrongLocationMsg = (modelLabel, wrongLocs) => (
        wrongLocs.length > 0 && (
            <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '5px' }}>
                <span>
                    {modelLabel} extracted the following entities in the wrong location:
                </span>
                <span style={{ color: 'gray' }}>
                    {wrongLocs.map((ent, idx) => (
                        <span key={idx}>
                            {ent?.predicted_label}: {ent?.details?.actual_content};{' '}
                        </span>
                    ))}
                </span>
            </div>
        )
    );

    // Square top corners so the frame's border runs straight up into the active
    // card tab (the default 12px radius curves away under the leftmost tab).
    const tabConnectedCardStyle = { borderTopLeftRadius: 0, borderTopRightRadius: 0 };

    const entityFilterItems = [
        { id: 'all', label: 'All entities predicted' },
        ...uniqueEntityLabels.map(label => ({
            id: label,
            label: `${label} (${labelCounts[label] || 0})`,
        })),
    ];
    const selectedEntityFilterLabel = entityFilterItems.find(item => item.id === selectedEntityFilter)?.label;

    return (
        <SparkModalPortal open={visible} onClose={onClose} size="wide">
            <Modal
                title={(activeTab === 'relations') ? 'Relation Analysis' : 'Text Sample'}
                showSubtitle={false}
                showFooter={false}
                fixedHeight={false}
                onCloseClick={onClose}
            >
            {/* Card tabs on the left, entity filter on the right — one row (entities and relations share the same tabs UI) */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <Tabs
                        variant="card"
                        size="small"
                        items={[
                            { id: '1', label: <span className='fs-unmask'>True Entities</span> },
                            ...(comparisonModelName && !teacherModelProcessing ? [{ id: '3', label: <span className='fs-unmask'>{comparisonModelName}</span> }] : []),
                            ...(!studentModelProcessing ? [{ id: '2', label: <span className='fs-unmask'>SpecificAI V{version}</span> }] : []),
                        ]}
                        activeId={modalActiveTab}
                        onChange={setModalActiveTab}
                    />
                </div>
                <div style={{ width: 220, flexShrink: 0, marginBottom: 6 }}>
                    <DropdownMenu
                        items={entityFilterItems}
                        value={selectedEntityFilterLabel}
                        onSelect={(item) => setSelectedEntityFilter(item.id)}
                        placeholder="Filter by entity"
                        showLabel={false}
                        size="small"
                    />
                </div>
            </div>
            {activeTab !== 'relations' && modalActiveTab === '1' && (
                <div className="optune-scrollbar" style={{ maxHeight: 360, overflowY: 'auto', marginTop: -1 }}>
                    <CardSurface style={tabConnectedCardStyle}>
                        <TaggedSample
                            text={sample?.request_example}
                            entities={getFilteredEntities(sample?.test_response)}
                            getTextDirectionStyle={getTextDirectionStyle}
                            modelName={"Ground Truth"}
                            allLabels={allEntities}
                            isGroundTruth
                            disableCard
                        />
                    </CardSurface>
                </div>
            )}
            {activeTab !== 'relations' && modalActiveTab === '3' && comparisonModelName && !teacherModelProcessing && (
                <div className="optune-scrollbar" style={{ maxHeight: 360, overflowY: 'auto', marginTop: -1 }}>
                    <CardSurface style={tabConnectedCardStyle}>
                        {comparisonEmptyInDb ? renderNoResponse() : (
                            <TaggedSample
                                text={sample?.request_example}
                                entities={getFilteredEntities(sample?.teacher_response || sample?.comparison_model_response)}
                                getTextDirectionStyle={getTextDirectionStyle}
                                modelName={comparisonModelName}
                                allLabels={allEntities}
                                entityStatuses={getFilteredEntityStatuses(sample?.teacher_model_entities_status || sample?.comparison_model_entities_status || [])}
                                disableCard
                                suppressWrongLocationMessage
                            />
                        )}
                    </CardSurface>
                    {!comparisonEmptyInDb && renderWrongLocationMsg(comparisonModelName, oldModelWrongLocations)}
                    {!comparisonEmptyInDb && oldModelParseFailed && renderParseFailedMsg(comparisonModelName, oldModelParseFailedText)}
                </div>
            )}
            {activeTab !== 'relations' && modalActiveTab === '2' && !studentModelProcessing && (
                <div className="optune-scrollbar" style={{ maxHeight: 360, overflowY: 'auto', marginTop: -1 }}>
                    <CardSurface style={tabConnectedCardStyle}>
                        {studentEmptyInDb ? renderNoResponse() : (
                            <TaggedSample
                                text={sample?.request_example}
                                entities={getFilteredEntities(sample?.new_model_response)}
                                getTextDirectionStyle={getTextDirectionStyle}
                                modelName={`SpecificAI V${version}`}
                                allLabels={allEntities}
                                entityStatuses={getFilteredEntityStatuses(sample?.new_model_entities_status || [])}
                                disableCard
                                suppressWrongLocationMessage
                            />
                        )}
                    </CardSurface>
                    {!studentEmptyInDb && renderWrongLocationMsg(`SpecificAI V${version}`, newModelWrongLocations)}
                    {!studentEmptyInDb && newModelParseFailed && renderParseFailedMsg(`SpecificAI V${version}`, newModelParseFailedText)}
                </div>
            )}

            {
                activeTab === 'relations' && (
                    <div style={{ display: 'flex', gap: 16 }}>
                        {/* Relations found */}
                        <div style={{}}>
                            <div size="small" style={{ marginBottom: 12 }}>
                                <div style={{ fontWeight: 300, marginBottom: 8, fontSize: 14 }}>Relations found ({relationsForActiveTab.length})</div>
                                {relationsForActiveTab.length === 0 ? (
                                    <div style={{ border: '1px solid #e8e8e8', borderRadius: 2, padding: 20, color: '#6d6d6d', textAlign: 'center' }}>
                                        <div>Relations will be</div>
                                        <div>shown here.</div>
                                    </div>
                                ) : (
                                    <div className="optune-scrollbar" style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 450, overflowY: 'auto' }}>
                                        {relationsForActiveTab.map((rel, idx) => (
                                            <CardSurface key={idx} style={{ padding: 12 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: "center" }}>
                                                    {rel.sourceInvalid ? (
                                                        <TagColored label={rel.source} variant="volcano" />
                                                    ) : (
                                                        <TooltipTrigger text={rel.sourceLabel}>
                                                            <TagColored label={rel.source} variant={getSparkTagVariant(rel.sourceLabel, allEntities || [])} />
                                                        </TooltipTrigger>
                                                    )}
                                                    <SparkIcon slug="arrow-right" size="small" />
                                                    {rel.targetInvalid ? (
                                                        <TagColored label={rel.target} variant="volcano" />
                                                    ) : (
                                                        <TooltipTrigger text={rel.targetLabel}>
                                                            <TagColored label={rel.target} variant={getSparkTagVariant(rel.targetLabel, allEntities || [])} />
                                                        </TooltipTrigger>
                                                    )}
                                                </div>
                                                <div style={{ marginTop: 8, display: "flex", justifyContent: "center" }}>
                                                    {rel?.errorType ? (
                                                        <TooltipTrigger text={rel?.tooltip}>
                                                            <TagColored label={rel.type} variant="volcano" />
                                                        </TooltipTrigger>
                                                    ) : (
                                                        <TagColored label={rel.type} variant="disabled" />
                                                    )}
                                                </div>
                                            </CardSurface>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Text sample */}
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 300, marginBottom: 8, fontSize: 14 }}>Text sample</div>
                            <div className="optune-scrollbar" style={{ maxHeight: 360, overflowY: 'auto' }}>
                                <CardSurface style={{ padding: 12 }}>
                                    {modalActiveTab === '1' && (
                                        <TaggedSample
                                            text={sample?.request_example}
                                            entities={getFilteredEntities(sample?.test_response)}
                                            getTextDirectionStyle={getTextDirectionStyle}
                                            modelName={"Ground Truth"}
                                            allLabels={allEntities}
                                            isGroundTruth
                                            disableCard
                                        />
                                    )}
                                    {modalActiveTab === '2' && (
                                        studentEmptyInDb ? renderNoResponse() : (
                                            <TaggedSample
                                                text={sample?.request_example}
                                                entities={getFilteredEntities(sample?.new_model_response)}
                                                getTextDirectionStyle={getTextDirectionStyle}
                                                modelName={`SpecificAI V${version}`}
                                                allLabels={allEntities}
                                                entityStatuses={getFilteredEntityStatuses(sample?.new_model_entities_status || [])}
                                                disableCard
                                                suppressWrongLocationMessage
                                            />
                                        )
                                    )}
                                    {modalActiveTab === '3' && (
                                        comparisonEmptyInDb ? renderNoResponse() : (
                                            <TaggedSample
                                                text={sample?.request_example}
                                                entities={getFilteredEntities(sample?.teacher_response || sample?.comparison_model_response)}
                                                getTextDirectionStyle={getTextDirectionStyle}
                                                modelName={comparisonModelName}
                                                allLabels={allEntities}
                                                entityStatuses={getFilteredEntityStatuses(sample?.teacher_model_entities_status || sample?.comparison_model_entities_status || [])}
                                                disableCard
                                                suppressWrongLocationMessage
                                            />
                                        )
                                    )}
                                </CardSurface>
                                {modalActiveTab === '2' && !studentEmptyInDb && newModelParseFailed && (
                                    renderParseFailedMsg(`SpecificAI V${version}`, newModelParseFailedText)
                                )}
                                {modalActiveTab === '2' && !studentEmptyInDb && renderWrongLocationMsg(`SpecificAI V${version}`, newModelWrongLocations)}
                                {modalActiveTab === '3' && !comparisonEmptyInDb && oldModelParseFailed && (
                                    renderParseFailedMsg(comparisonModelName, oldModelParseFailedText)
                                )}
                                {modalActiveTab === '3' && !comparisonEmptyInDb && renderWrongLocationMsg(comparisonModelName, oldModelWrongLocations)}
                            </div>

                            {relationTypesCount && Object.keys(relationTypesCount).length > 0 && <div style={{ marginTop: 12 }}>
                                <div style={{ marginBottom: 8, fontWeight: 300 }}>Relation Types</div>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    {Object.entries(relationTypesCount).map(([label, total]) => (
                                        <TagColored key={label} label={`${label} (${total ?? 0})`} variant="disabled" />
                                    ))}
                                </div>
                            </div>}
                        </div>
                    </div>
                )
            }
            </Modal>
        </SparkModalPortal>
    );
};

export default EntityModal;
